import { Fragment, ReactNode, useEffect, useState } from 'react';
import { FormGroup, RadioGroup } from '@mui/material';
import Checkbox from '../../../../common/Checkbox/Checkbox';
import styles from './Facet.module.scss';
import { RightArrow } from '../../../../../lib/icons/RightArrow';
import { classes } from '../../../../../lib/utils/cssUtils';
import { FILTER_LABELS } from '../../../../../lib/constants/labelKeys';
import { Label } from '../../../../common/Label/Label';
import { updateByProp } from '../../../../../lib/utils/updateByProp';
import { filterIds } from '../../../../../lib/tests/testIds';
import { Facet, FacetDateItem, FacetItem, FacetType } from '../../../../../lib/utils/search/searchModel';
import { useFiltersView } from '../../../../../lib/context/FiltersViewContext';
import useDatePicker from '../../../../../lib/hooks/useDatePicker';
import Radio from '../../../../common/Radio/Radio';
import LazyDateRangePicker from '../../DateRangePicker/LazyDateRangePicker';
import { useDevice } from '../../../../../lib/hooks/device';
import { isNil } from '../../../../../lib/utils/isNil';

export type FacetProps<T extends FacetType = FacetType> = Facet<T> & {
  onChange: <U extends FacetType>(selected: Facet<U>) => void;
};

const isCheckbox = (type: FacetType | undefined, values: FacetProps['values']): values is FacetItem[] =>
  type !== FacetType.Radio;

const isRadio = (type: FacetType | undefined, values: FacetProps['values']): values is FacetDateItem[] =>
  type === FacetType.Radio;

const isDatePickerSelected = (values: FacetDateItem[]) => values.find(item => item.isDatePicker && item.selected);

const FacetView = <T extends FacetType>({ name, values, type, onChange }: FacetProps<T>): ReactNode => {
  const {
    visibleFacetOptionsNumber,
    filterGroupsCollapsedDesktop = false,
    filterGroupsCollapsedMobile = true,
  } = useFiltersView();
  const device = useDevice();
  const [isExpanded, setIsExpanded] = useState(
    device === 'desktop' ? !filterGroupsCollapsedDesktop : !filterGroupsCollapsedMobile,
  );
  const [showAllValues, setShowAllValues] = useState(false);
  const { startDate, setStartDate, endDate, setEndDate } = useDatePicker();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const itemsContainerId = `${name}-items`;

  useEffect(() => {
    if (isRadio(type, values) && isDatePickerSelected(values)) {
      onChange<typeof FacetType.Radio>({
        name,
        values: values.map(item => (item.isDatePicker ? { ...item, from: startDate, to: endDate } : { ...item })),
        type: FacetType.Radio,
      });
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (isRadio(type, values)) {
      const selectedItem = values.find(item => item.selected);
      const datePickerItem = values.find(item => item.isDatePicker);
      if (selectedItem && selectedItem.label !== selectedDate) {
        setSelectedDate(selectedItem.label);
      }
      if (!selectedItem) {
        setSelectedDate(null);
      }
      if (datePickerItem && !datePickerItem.from && !datePickerItem.to) {
        setStartDate(null);
        setEndDate(null);
      }
    }
  }, [values]);

  useEffect(() => {
    setIsExpanded(device === 'desktop' ? !filterGroupsCollapsedDesktop : !filterGroupsCollapsedMobile);
  }, [device, filterGroupsCollapsedDesktop, filterGroupsCollapsedMobile]);

  if (!values.length) {
    return null;
  }

  const handleChange = (updatedValue: FacetItem) => {
    if (isCheckbox(type, values)) {
      onChange({
        name,
        values: updateByProp(values, updatedValue, 'value'),
      });
    }
  };

  const handleRadioChange = (updatedValue: FacetDateItem) => {
    if (isRadio(type, values)) {
      const newValues = values.map(item => {
        if (item.label === updatedValue.label) {
          return updatedValue.isDatePicker ? { ...updatedValue, from: startDate, to: endDate } : { ...updatedValue };
        }

        return { ...item, selected: false };
      });
      onChange<typeof FacetType.Radio>({
        name,
        values: newValues,
        type: FacetType.Radio,
      });
    }
  };

  return (
    <div className={styles.facetContainer} data-testid={filterIds.facetGroup}>
      <button
        className={styles.facetTitle}
        onClick={() => setIsExpanded(prev => !prev)}
        aria-expanded={isExpanded}
        aria-controls={itemsContainerId}
      >
        <Label name={FILTER_LABELS.FACET_LABEL(name)} />
        <RightArrow className={classes(styles.arrow, { [styles.arrowExpanded]: isExpanded })} />
      </button>
      {isCheckbox(type, values) ? (
        <FormGroup
          id={itemsContainerId}
          className={classes(styles.facetItems, { [styles.expandedFacetItems]: isExpanded })}
        >
          {values
            .slice(0, showAllValues ? values.length : visibleFacetOptionsNumber)
            .map(({ value, count, selected, label }) => (
              <Checkbox
                className={styles.facetItem}
                key={value}
                label={
                  <>
                    <Label name={FILTER_LABELS.FACET_VALUE(name, value)} fallback={label ?? value} />
                    {!isNil(count) && ` (${count})`}
                  </>
                }
                onSelect={state =>
                  handleChange({ value, count, ...(label && { label }), ...(count && { count }), selected: state })
                }
                selected={selected ?? true}
              />
            ))}
        </FormGroup>
      ) : (
        <RadioGroup
          value={selectedDate}
          id={itemsContainerId}
          className={classes(styles.facetItems, { [styles.expandedFacetItems]: isExpanded })}
        >
          {values.slice(0, showAllValues ? values.length : visibleFacetOptionsNumber).map(value => (
            <Fragment key={value.label}>
              <Radio
                formControlLabelProps={{
                  onClick: () => handleRadioChange({ ...value, selected: true }),
                  value: value.label,
                  label: <Label name={FILTER_LABELS.FACET_VALUE(name, value.label)} fallback={value.label} />,
                  className: classes(styles.facetItem, styles.radioFacet),
                }}
              />
              {value.isDatePicker && (
                <LazyDateRangePicker
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  disabled={!value.selected}
                  buttonClasses={styles.datePicker}
                  shortcuts={value.shortcuts}
                />
              )}
            </Fragment>
          ))}
        </RadioGroup>
      )}
      {values.length > visibleFacetOptionsNumber && !showAllValues && (
        <button
          className={classes(styles.showMoreBtn, { [styles.hideShowMoreBtn]: !isExpanded })}
          onClick={() => setShowAllValues(true)}
        >
          <Label name={FILTER_LABELS.SHOW_MORE_FACET_VALUES} />
        </button>
      )}
    </div>
  );
};

export default FacetView;
